import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { searchChat } from "../../../actions/chatActions";

const formatDate = (value) => {
    if (!value) return "";

    return new Date(value).toLocaleString("ru-RU", {
        day: "2-digit",
        month: "2-digit",
        year: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
    });
};

const formatCallStatus = (status) => ({
    ringing: "вызов",
    accepted: "принят",
    missed: "пропущен",
    ended: "завершён",
    rejected: "отклонён",
    cancelled: "отменён",
}[status] || status || "—");

export default function SearchModal({ chatId, onClose }) {
    const dispatch = useDispatch();
    const scrollRef = useRef(null);
    const loadingRef = useRef(false);

    const [q, setQ] = useState("");
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");
    const [starred, setStarred] = useState(false);
    const [items, setItems] = useState([]);
    const [cursor, setCursor] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState("");
    const [searched, setSearched] = useState(false);

    const load = useCallback(async (before = null, replace = true) => {
        if (!chatId || loadingRef.current) return;

        loadingRef.current = true;
        setLoading(true);
        setError("");

        try {
            const data = await dispatch(searchChat(chatId, {
                q: q.trim(),
                dateFrom: dateFrom || undefined,
                dateTo: dateTo || undefined,
                starred,
                before,
                limit: 40,
            }));

            const nextItems = Array.isArray(data?.items) ? data.items : [];
            setItems((prev) => replace ? nextItems : [...prev, ...nextItems]);
            setCursor(data?.nextCursor || null);
            setSearched(true);
        } catch (requestError) {
            setError(requestError?.response?.data?.error || requestError?.message || "Не удалось выполнить поиск");
            if (replace) setItems([]);
            setCursor(null);
            setSearched(true);
        } finally {
            loadingRef.current = false;
            setLoading(false);
        }
    }, [chatId, dispatch, q, dateFrom, dateTo, starred]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return undefined;

        const handler = () => {
            if (loadingRef.current || !cursor) return;
            if (el.scrollTop + el.clientHeight >= el.scrollHeight - 120) load(cursor, false);
        };

        el.addEventListener("scroll", handler, { passive: true });
        return () => el.removeEventListener("scroll", handler);
    }, [cursor, load]);

    useEffect(() => {
        const handler = (event) => {
            if (event.key === "Escape") onClose?.();
        };

        window.addEventListener("keydown", handler);
        return () => window.removeEventListener("keydown", handler);
    }, [onClose]);

    return (
        <div className="chat-modal-AO" onMouseDown={onClose}>
            <div className="chat-modal-AO__body chat-modal-AO__body--search" onMouseDown={(event) => event.stopPropagation()}>
                <div className="chat-modal-AO__head">
                    <div>
                        <div className="chat-modal-AO__title">Поиск по чату</div>
                        <div className="chat-modal-AO__subtitle">Текст, файлы, даты и избранные сообщения.</div>
                    </div>
                    <button type="button" onClick={onClose}>✕</button>
                </div>

                <form
                    className="chat-search-AO"
                    onSubmit={(event) => {
                        event.preventDefault();
                        load(null, true);
                    }}
                >
                    <input type="text" value={q} placeholder="Текст или имя файла…" onChange={(event) => setQ(event.target.value)} />
                    <input type="date" value={dateFrom} onChange={(event) => setDateFrom(event.target.value)} aria-label="Дата от" />
                    <input type="date" value={dateTo} onChange={(event) => setDateTo(event.target.value)} aria-label="Дата до" />
                    <label>
                        <input type="checkbox" checked={starred} onChange={(event) => setStarred(event.target.checked)} />
                        Избранные
                    </label>
                    <button type="submit" disabled={loading}>{loading ? "Ищем…" : "Искать"}</button>
                </form>

                {error && <div className="chat-search-AO__error" role="alert">{error}</div>}

                <div ref={scrollRef} className="chat-search-AO__results">
                    {items.map((item) => (
                        <div key={`${item.type}-${item._id}`} className="chat-search-AO__item">
                            <div className="chat-search-AO__date">{formatDate(item.createdAt)}</div>
                            {item.type === "call" ? (
                                <div className="chat-search-AO__text">Звонок · {formatCallStatus(item.call?.status)}</div>
                            ) : (
                                <div className="chat-search-AO__text">{item.text || item.entity?.title || item.fileName || "Вложение"}</div>
                            )}
                        </div>
                    ))}

                    {loading && <div className="chat-modal-AO__hint">Загрузка…</div>}
                    {!loading && searched && !items.length && !error && <div className="chat-modal-AO__hint">Ничего не найдено</div>}
                    {!loading && !searched && <div className="chat-modal-AO__hint">Задайте параметры и нажмите «Искать».</div>}
                </div>
            </div>
        </div>
    );
}
